import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Link,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiClient } from '../services/apiClient';
import type { PasswordResetValidateResponse } from '../services/types';

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();

  const token = searchParams.get('token') || '';

  const [emailPreview, setEmailPreview] = useState<string | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [loadingToken, setLoadingToken] = useState(true);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!token) {
      setTokenError('No reset token found. Use the link from your password reset email.');
      setLoadingToken(false);
      return;
    }

    apiClient
      .get<PasswordResetValidateResponse>(`/api/v1/auth/reset-password/${token}`)
      .then((res) => setEmailPreview(res.data.email))
      .catch((err) => {
        const detail = err.response?.data?.detail;
        if (err.response?.status === 404) {
          setTokenError(detail || 'This reset link is invalid or has expired.');
        } else {
          setTokenError(detail || 'Failed to validate reset link. Please try again.');
        }
      })
      .finally(() => setLoadingToken(false));
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (password !== confirmPassword) {
      setSubmitError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setSubmitError('Password must be at least 8 characters.');
      return;
    }

    setSubmitting(true);
    try {
      await apiClient.post('/api/v1/auth/reset-password', { token, password });
      navigate('/login', { replace: true, state: { resetSuccess: true } });
    } catch (err: unknown) {
      const detail =
        typeof err === 'object' && err !== null && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : undefined;
      setSubmitError(detail || 'Could not reset password. The link may have expired.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        padding: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 420,
          width: '100%',
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          background: 'rgba(255, 255, 255, 0.95)',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              component="img"
              src="/images/migratr.ai L.png"
              alt="migratr"
              sx={{ width: 240, height: 'auto', objectFit: 'contain' }}
            />
          </Box>
          <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
            Choose a new password
          </Typography>

          {loadingToken && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {!loadingToken && tokenError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {tokenError}
            </Alert>
          )}

          {!loadingToken && !tokenError && emailPreview && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Account: <strong>{emailPreview}</strong>
            </Typography>
          )}

          {!loadingToken && !tokenError && emailPreview && (
            <form onSubmit={handleSubmit}>
              {submitError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {submitError}
                </Alert>
              )}
              <TextField
                fullWidth
                label="New password"
                variant="outlined"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
                sx={{ mb: 2 }}
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" disabled={submitting}>
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label="Confirm password"
                variant="outlined"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={submitting}
                sx={{ mb: 2 }}
                autoComplete="new-password"
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={submitting || !password || !confirmPassword}
                sx={{
                  py: 1.5,
                  background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                  },
                }}
              >
                {submitting ? <CircularProgress size={24} color="inherit" /> : 'Update password'}
              </Button>
            </form>
          )}

          <Typography variant="body2" sx={{ textAlign: 'center', mt: 2 }}>
            <Link component={RouterLink} to="/login" underline="hover">
              Back to sign in
            </Link>
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 3 }}>
            © 2026 Aventior. All rights reserved.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ResetPasswordPage;
